import { FC } from 'react';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { UserRole } from '../../types/user.types';
import { displayDate } from '../../util/dateTime';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useTable } from '../../util/useTable';
import {
  Checkbox,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  TablePagination,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { Edit3, PenTool, Trash2 } from 'react-feather';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { DialogModal } from '../../components';
import { FormEditModal } from './FormEditModal';
import { useEffect, useState } from 'react';
import {
  FormsResponse,
  useDeleteForms,
  useGetPaginateForms,
} from '../../api-http/forms';
import { AlertBar } from '../../components/AlertBar';
import {
  BulkSelectionBar,
  managementTableSx,
} from '../../components/ManagementPageLayout';
import { useSnack } from '../../util/useSnack';
import { notifyMsg } from '../../configs';
import { useConfirm } from 'material-ui-confirm';
import { OrderEnum, SortEnum } from '../../types/pagination.types';

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

interface TableProp {
  refresh: boolean;
  search: string;
}

const FormsTable: FC<TableProp> = ({ refresh, search }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const isViewOnly = useIsUserRole([UserRole.user, UserRole.dataEditor]);
  const disableEdit = useIsUserRole([UserRole.user]);
  const { showErrSnack, showSuccessSnack } = useSnack();
  const location = useLocation();

  const { tableProps, pagingParams, sortOnClick } = useTable(true);

  const [activeForm, setActiveForm] = useState<FormsResponse | undefined>();
  const { sortby, order } = pagingParams;
  const [formEditModal, setFormEditModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const {
    data: list,
    isLoading: listIsLoading,
    refetch,
    error,
  } = useGetPaginateForms({ ...pagingParams, search: search });

  const {
    error: deleteError,
    isLoading: isDeleteLoading,
    mutateAsync: deleteForm,
    data: deletedForms,
  } = useDeleteForms({
    onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
  });

  const handleDelete = async (id: string) => {
    try {
      await confirm({
        description: notifyMsg.DELETE_CONFIRMATION,
        confirmationText: 'Yes',
      });
      await deleteForm({ id });
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      await refetch();
      showSuccessSnack(notifyMsg.FORM_DELETE_SUCCESS);
    } catch (e) {}
  };

  useEffect(() => {
    refetch();
    setSelectedIds(new Set());
  }, [deletedForms, refresh]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [search, pagingParams.page]);

  const forms = list?.data || [];
  const pageIds = forms.map((form) => form._id);
  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someSelected = pageIds.some((id) => selectedIds.has(id));

  const setSelected = (id: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(pageIds) : new Set());
  };

  const removeSelected = async () => {
    if (!selectedIds.size) return;
    try {
      await confirm({
        title: `Delete ${selectedIds.size} forms?`,
        description:
          'The selected forms will be permanently deleted. This action cannot be undone.',
        confirmationText: 'Delete selected',
      });
      for (const id of Array.from(selectedIds)) {
        await deleteForm({ id });
      }
      setSelectedIds(new Set());
      await refetch();
      showSuccessSnack('Selected forms deleted');
    } catch (e) {}
  };

  return (
    <>
      {(listIsLoading || isDeleteLoading) && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg || deleteError?.msg} />
      <BulkSelectionBar
        count={selectedIds.size}
        singularLabel="form"
        pluralLabel="forms"
        onClear={() => setSelectedIds(new Set())}
        onDelete={removeSelected}
        disabled={disableEdit || isDeleteLoading}
      />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={(event) => toggleAll(event.target.checked)}
                  disabled={disableEdit || !pageIds.length}
                  inputProps={{ 'aria-label': 'Select all forms' }}
                />
              </StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.name || true}
                  direction={sortby === SortEnum.name ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.name)}
                >
                  Form title
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt || true}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.createdAt || true}
                  direction={
                    sortby === SortEnum.createdAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.createdAt)}
                >
                  Created
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">Usage</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.map((form: FormsResponse) => {
              const { _id, name, updatedAt, createdAt, device } = form;
              return (
                <TableRow
                  key={_id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(_id)}
                      onChange={(event) =>
                        setSelected(_id, event.target.checked)
                      }
                      disabled={disableEdit}
                      inputProps={{ 'aria-label': `Select ${name}` }}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {isViewOnly ? (
                      name
                    ) : (
                      <NavLink
                        to={`/forms/responses/${_id}`}
                        state={{
                          search: location?.search,
                        }}
                      >
                        {name}
                      </NavLink>
                    )}
                  </TableCell>
                  <TableCell align="left">{displayDate(updatedAt)}</TableCell>
                  <TableCell align="left">{displayDate(createdAt)}</TableCell>
                  <TableCell align="left">
                    <Chip
                      size="small"
                      label={device?.length === 0 ? 'Not used' : 'In use'}
                      sx={{
                        bgcolor: device?.length === 0 ? '#f1f5f9' : '#dcfce7',
                        color: device?.length === 0 ? '#475569' : '#15803d',
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="Edit form">
                        <span>
                          <IconButton
                            aria-label={`Edit ${name}`}
                            disabled={disableEdit}
                            onClick={() => navigate(`/survey-editor/${_id}`)}
                            sx={{ ...actionButtonSx, color: '#2563eb' }}
                          >
                            <PenTool size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Rename form">
                        <span>
                          <IconButton
                            aria-label={`Rename ${name}`}
                            disabled={disableEdit}
                            onClick={() => {
                              setActiveForm(form);
                              setFormEditModal(true);
                            }}
                            sx={{ ...actionButtonSx, color: '#64748b' }}
                          >
                            <Edit3 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete form">
                        <span>
                          <IconButton
                            aria-label={`Delete ${name}`}
                            disabled={disableEdit}
                            onClick={() => handleDelete(_id)}
                            sx={{
                              ...actionButtonSx,
                              color: '#dc2626',
                              bgcolor: '#fffafa',
                              borderColor: '#fecaca',
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
        />
      </TableContainer>
      <DialogModal
        title="Rename form"
        onClose={() => setFormEditModal(false)}
        open={formEditModal}
      >
        <FormEditModal
          forms={activeForm}
          onFinished={() => {
            setFormEditModal(false);
          }}
          refetch={refetch}
        />
      </DialogModal>
    </>
  );
};

export default FormsTable;
